import type { QueryClient } from '@tanstack/react-query';
import { applyCachePatches, type CachePatch } from '@/cache/patches';

/**
 * # The cache-patch seam
 *
 * Everything that changes mail state in this app — an optimistic mutation, a
 * patch broadcast from another window, and now an event pushed from the server
 * over SSE (ADR 0002) — writes through *this* sink, applying the *same*
 * `CachePatch` values. That is the whole point: an archive performed in this
 * tab and an archive pushed from another device converge on byte-identical
 * cache state, because they are the same values applied by the same code.
 *
 * This module replaces the WebSocket-era `seam.ts`. Its contract survived the
 * transport change unchanged, which is the evidence that describing *what
 * changed in the world* rather than *which caches to poke* was the right shape:
 * the reducer in `reducer.ts` is still a pure function from an event to
 * `CachePatch[]`, and the connection module still does nothing to the cache
 * except hand patches to this sink.
 *
 * ## The rule this seam enforces
 *
 * **Never blanket-invalidate a visible list.** An `invalidateQueries` on a
 * message list the user is scrolled into causes a refetch, a re-render of every
 * virtualized row, and a scroll jump. The sink offers no invalidation path at
 * all: an event either patches, or it does nothing.
 *
 * Recovery from a *gap* — `resync_required`, `folder_invalidated` — is the one
 * documented exception, and it deliberately does not come through here. It is a
 * `RealtimeEffect` (see `reducer.ts`), applied by `applyRealtimeEffects`, so
 * that "this drops cache entries" is a visibly different thing from "this
 * patches them" rather than a patch with surprising reach.
 */
export type RealtimeCachePatchSink = (patches: readonly CachePatch[]) => void;

/**
 * Builds the sink the realtime reducer writes through.
 *
 * Deliberately a closure over the `QueryClient` rather than a hook: the stream
 * handler is not a React component, and the patch layer must stay callable from
 * plain TypeScript (and from tests) without a renderer.
 */
export function createRealtimeCachePatchSink(client: QueryClient): RealtimeCachePatchSink {
  return (patches) => applyCachePatches(client, patches);
}
